import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRelatedContentAuxiliar } from '../../../../modules/Disciplines/hooks/useRelatedContentAux';
import formatFileCategory from '../../../commom/formatFileCategory';
import { formatTime } from '../../../commom/formatTime';
import { useFetchLessonQuiz } from '../../../hooks/useFetchLessonQuiz';
import { Colors, IMaterial } from '../../../models/disciplines';
import { IExercise } from '../../../models/exercise';
import { PlayerType } from '../../../models/video';
import { Button } from '../../atoms/Button';
import { Tag, TagProps } from '../../atoms/Tag';
import Timeline from '../../atoms/Timeline';
import BrainLoadingWrapper from '../../molecules/BrainLoading';
import PageHeader from '../../molecules/Header';
import InnerHTMLContainer from '../../molecules/InnerHTMLContainer';
import Player from '../../molecules/Player';
import PlayerAprova from '../../atoms/PlayerAprova';
import PlayerLiveChat from '../../molecules/PlayerLiveChat';
import { PlayerQuiz } from '../../molecules/PlayerQuiz';
import { Typography } from '../../molecules/Typography';
import { Breadcrumbs, BreadcrumbsItem } from '../Breadcrumbs';
import { ButtonGroup } from '../ButtonGroup';
import Comment from '../comment';
import { ContentActions } from '../ContentActions';
import ContentCard from '../ContentCard';
import { useNewSubjectStore } from '../../../../modules/Disciplines/stores/newSubjectStore';
import { ISubject } from '../../../models/subject';

interface Subject {
  title: string;
}

interface ILesson {
  id: string;
  slug?: string;
  title: string;
  module?: string;
  discipline: string;
  subjects?: string | Subject[];
  subjectRelated?: string;
  time?: string | number;
  teacher: string;
  pdfSummary?: string;
  review?: 'like' | 'dislike' | undefined;
  isFavorite?: boolean;
  timeline: Array<any>;
  playerType: PlayerType;
  token: string;
  embedUrl?: string;
  defaultPlayer?: PlayerType;
  breadcrumb?: Array<BreadcrumbsItem>;
  incidence?: string;
  timelineText?: string;
  onFinish?: () => any;
  showFavorite?: boolean;
  contentSlug?: string;
  tags?: TagProps[];
  recordedAt?: string;

  contentType?: 'Lesson' | 'Material' | 'Live';

  isDisciplines?: boolean;
  isLiveStream?: boolean;
  showLiveChat?: boolean;

  showQuiz?: boolean;
  onFullFinish?: () => any;
}

export default function Lesson({
  id,
  title,
  discipline,
  incidence,
  time,
  teacher,
  pdfSummary,
  timeline = [],
  timelineText,
  token,
  playerType,
  embedUrl,
  breadcrumb = [],
  isFavorite,
  review,
  onFinish,
  showFavorite = true,
  contentSlug,
  tags = [],
  recordedAt,
  isLiveStream = false,
  showLiveChat = false,
  isDisciplines = false,
  contentType,
  subjects,
  subjectRelated,
  module,
  showQuiz = false,
  onFullFinish,
}: ILesson) {
  const navigate = useNavigate();

  const [timeLesson, setTime] = useState('');
  const [seek, setSeek] = useState(0);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isQuizOpen, setIsQuizOpen] = useState(false);

  // Resolve o player uma única vez: prioriza o novo player (embedUrl); na falta
  // dele, usa o player antigo (vimeo/sambatech). Mantemos `null` até a validação
  // terminar para não trocar o player em tela (pop-in) para o usuário.
  const [resolvedPlayer, setResolvedPlayer] = useState<
    'aprova' | 'legacy' | null
  >(null);

  useEffect(() => {
    setResolvedPlayer(embedUrl ? 'aprova' : 'legacy');
  }, [embedUrl]);

  const { setSelectedSubjects } = useNewSubjectStore();
  
  const { mutate, data, isPending } = useRelatedContentAuxiliar();
  const { data: quiz } = useFetchLessonQuiz(id, showQuiz);

  const quizRef = useRef(quiz);
  
  useEffect(() => {
    quizRef.current = quiz;
  }, [quiz]);

  useEffect(() => {
    if (subjectRelated && module) {
      mutate({ module: module, subject: subjectRelated });
    }
  }, [subjectRelated]);

  useEffect(() => {
    setTime(formatTime(time || ''));
  }, [time]);

  function onChangeTimeline(data: any) {
    setSeek(data.seconds);
  }

  const newTags: TagProps[] = [...tags];

  if (incidence) {
    newTags.push({
      variant: 'incidencia4',
      children: incidence,
    });
  }

  function handleFullFinish() {
    const currentQuiz = quizRef.current;
    if (showQuiz && currentQuiz) {
      setIsQuizOpen(true);
    }

    onFullFinish && onFullFinish();
  }

  return (
    <div>
      <Breadcrumbs items={breadcrumb} className='pb-12' />

      <div className='pb-8'>
        <PageHeader title={title} module={discipline}>
          <div className='flex flex-col gap-md '>
            <div className='flex flex-wrap gap-sm'>
              {tags.map((tag, index) => (
                <Tag key={index} {...tag} />
              ))}
              {typeof subjects === 'string' ? (
                <Tag
                  variant={discipline as Colors}
                  className='flex flex-row items-center gap-sm px-2'
                >
                  <Typography size='small' className='text-[#fff]'>
                    {subjects}
                  </Typography>
                </Tag>
              ) : Array.isArray(subjects) ? (
                subjects &&
                subjects.map((subject: { title: string }, index: number) => (
                  <Tag
                    key={index}
                    variant={discipline as Colors}
                    className='flex flex-row items-center gap-sm px-2'
                  >
                    <Typography size='small' className='text-[#fff]'>
                      {subject.title}
                    </Typography>
                  </Tag>
                ))
              ) : null}
            </div>
            <div className='flex items-center justify-between gap-3 pt-1'>
              <div className='mr-auto flex justify-between gap-3'>
                {timeLesson && (
                  <Typography size='small'>{timeLesson}</Typography>
                )}
                {teacher && (
                  <Typography size='small'>Prof. {teacher}</Typography>
                )}
                {recordedAt && (
                  <Typography size='small'>{recordedAt}</Typography>
                )}
              </div>
              <ContentActions.Root
                contentId={id}
                contentSlug={contentSlug}
                contentType={contentType ? contentType : 'Lesson'}
                className={'ml-auto'}
              >
                {!isLiveStream && (
                  <ContentActions.ReviewButtons defaultReview={review} />
                )}

                {showFavorite && (
                  <ContentActions.FavoriteButton
                    defaultFavorite={isFavorite}
                    category={isLiveStream ? 'live' : 'lesson'}
                  />
                )}

                <ContentActions.Report />
              </ContentActions.Root>
            </div>
          </div>
        </PageHeader>
      </div>

      <div
        className='relative flex flex-col lg:flex-row'
        id='lesson-player-container'
        data-discipline={discipline}
        data-id={id}
        data-title={title}
      >
        {quiz && !isQuizOpen && (
          <Button
            variant='contained'
            LeftIcon={'quiz'}
            onClick={() => setIsQuizOpen(true)}
            negative
            className='group absolute top-5 right-5 z-[3]'
          >
            <span className='hidden group-hover:block'>
              Responda ao quiz da aula
            </span>
          </Button>
        )}

        {quiz && isQuizOpen ? (
          <PlayerQuiz
            quiz={quiz}
            isOpen={isQuizOpen}
            setIsOpen={setIsQuizOpen}
          />
        ) : resolvedPlayer === null ? (
          <div
            className='h-[465px] w-full animate-pulse bg-black'
            aria-busy
            aria-label='Carregando player'
          />
        ) : resolvedPlayer === 'aprova' ? (
          <PlayerAprova embedUrl={embedUrl} title={title} className='w-full' />
        ) : (
          <Player
            onFinish={onFinish}
            onFullFinish={() => {
              handleFullFinish();
            }}
            token={token}
            playerType={playerType}
            initialTime={seek}
            className='h-[465px]'
            contentId={id}
          />
        )}

        {isLiveStream && showLiveChat && (
          <PlayerLiveChat token={token} playerType={playerType} />
        )}
      </div>

      <Timeline
        items={timeline}
        label={timelineText}
        onClickItem={onChangeTimeline}
      />

      {quiz && (
        <div className='flex justify-center pt-8 md:hidden'>
          <Button
            variant='outlined'
            LeftIcon={'quiz'}
            onClick={() => setIsQuizOpen(true)}
            full
          >
            Responda ao quiz da aula
          </Button>
        </div>
      )}

      {pdfSummary && (
        <div className='flex justify-end pt-8'>
          <a href={pdfSummary} target='_blank' rel='noreferrer'>
            <Button LeftIcon='open_in_new' variant='text'>
              {isLiveStream ? 'Abrir material' : 'Anotações da aula'}
            </Button>
          </a>
        </div>
      )}

      {!isLiveStream && isDisciplines && (
        <div className='pb-16 pt-8'>
          <ButtonGroup
            className='mb-12'
            buttons={[
              {
                label: 'Conteúdos relacionados',
                onClick: () => setSelectedTab(0),
                selected: selectedTab === 0,
              },
              {
                label: 'Comentários',
                onClick: () => setSelectedTab(1),
                selected: selectedTab === 1,
              },
            ]}
          />
          {selectedTab === 0 && (
            <Fragment>
              <Typography
                type='title'
                size='h3'
                className='text-mono-color-black'
              >
                Conteúdos relacionados à videoaula
              </Typography>
              {isPending ? (
                <BrainLoadingWrapper />
              ) : (
                <div className='mb-8 mt-8 flex gap-xxxl xs:flex-col lg:flex-row'>
                  <div className='xs:basis-full lg:basis-1/2'>
                    <div className='mb-4 flex flex-row items-end justify-between'>
                      <Typography
                        type='titleSection'
                        size='h5'
                        className='text-mono-color-dark'
                      >
                        EXERCÍCIOS
                      </Typography>
                      {data?.exercise.length === 2 && (
                        <Link
                          to={`/disciplinas/${discipline}/modulo/${module}/tipo/exercicio?subject=${subjectRelated}`}
                          className='text-sm text-color-brand-primary'
                        >
                          Ver mais
                        </Link>
                      )}
                    </div>
                    <div className='flex flex-col gap-md'>
                      {data?.exercise.map((it: IExercise, i: number) => (
                        <ContentCard
                          color={discipline as Colors}
                          key={i}
                          content='Exercício'
                          favorite={it.favorite}
                          icon='edit'
                          action={() => {
                            setSelectedSubjects(it.subjects.map((subject: ISubject) => subject.slug));
                            navigate(`/disciplinas/${discipline}/modulo/${module}/assunto/${it.subjects[0].slug}/exercicio/${it.slug}`)
                          }}
                          subject={it.subjects[0].title}
                          id={it.id}
                          done={it.done}
                        >
                          <InnerHTMLContainer
                            data={it.statement}
                            type='contentCard'
                            align='left'
                            font='noto'
                            className='text-sm text-mono-color-darkest transition delay-100 ease-in-out group-hover:text-card-color-default'
                          />
                        </ContentCard>
                      ))}
                    </div>
                  </div>
                  <div className='xs:basis-full lg:basis-1/2'>
                    <div className='mb-4 flex flex-row items-end justify-between'>
                      <Typography
                        type='titleSection'
                        size='h5'
                        className='text-mono-color-dark'
                      >
                        MATERIAIS
                      </Typography>
                      {data?.workbooks.length === 2 && (
                        <Link
                          to={`/disciplinas/${discipline}/modulo/${module}/tipo/materiais`}
                          className='text-sm text-color-brand-primary'
                        >
                          Ver mais
                        </Link>
                      )}
                    </div>
                    <div className='flex flex-col gap-md'>
                      {data?.workbooks.map((it: IMaterial, i: number) => (
                        <ContentCard
                          color={discipline as Colors}
                          key={i}
                          title={it.title}
                          done={it.done}
                          favorite={it.favorite}
                          icon='article'
                          content={'materiais'}
                          action={() =>
                            navigate(
                              `/disciplinas/${discipline}/modulo/${module}/assunto/${it.subjects[0].slug}/material/${it.slug}`
                            )
                          }
                          subject={it.subjects[0].title}
                          id={it.id}
                        >
                          <Typography
                            size='small'
                            className=' transition delay-100 ease-in-out group-hover:text-card-color-default'
                          >
                            {formatFileCategory(it.file_category)}
                          </Typography>
                        </ContentCard>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </Fragment>
          )}
          {selectedTab === 1 && (
            <Comment
              title='Comentários da videoaula'
              targetId={id}
              targetType={
                contentType && contentType !== 'Material'
                  ? contentType
                  : 'Lesson'
              }
            />
          )}
        </div>
      )}

      {!isLiveStream && !isDisciplines && (
        <div className='pb-16 pt-8'>
          <Comment
            title='Comentários da videoaula'
            targetId={id}
            targetType={
              contentType && contentType !== 'Material' ? contentType : 'Lesson'
            }
          />
        </div>
      )}
    </div>
  );
}
